import { Navigate, RouteObject } from "react-router-dom";
import AppLayout from "../layout/AppLayout";
import PublicLayout from "../layout/PublicLayout";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import PrivacyInfo from "../pages/PrivacyInfo";
import LegalInfo from "../pages/LegalInfo";
import Dashboard from "../pages/Dashboard";
import NotFound from "../pages/404";
import UseConditions from "../pages/UseConditions";
import FAQ from "../pages/FAQ";
import SupportForm from "../pages/SupportForm";
import { Helmet } from "react-helmet-async";
import Login from "../pages/Login";
import RouteGuard from "./RouteGuard";
import BookingsList from "../pages/Bookings/BookingsList";
import CoachesList from "../pages/Coaches/CoachesList";
import UsersList from "../pages/Users/UsersList";
import CompleteRegistration from "../pages/Users/CompleteRegistration";
import StructureData from "../pages/StructureData/StructureData";
import FacilitiesList from "../pages/Facilities/FacilitiesList";
import FacilityEdit from "../pages/Facilities/FacilityEdit";
import FacilityDetail from "../pages/Facilities/FacilityDetail";
import TournamentsList from "../pages/Tournaments/TournamentsList";
import TournamentEdit from "../pages/Tournaments/TournamentEdit";
import TournamentDetail from "../pages/Tournaments/TournamentDetail";
import BillingInfos from "../pages/BillingInfos/BillingInfos";
import RankingsList from "../pages/Rankings/RankingsList";
import AdminGuard from "./AdminGuard";
import GenerateSchedule from "../pages/Tournaments/GenerateSchedule";
import TournamentBrackets from "../pages/Tournaments/TournamentBrackets";
import BulkBooking from "../pages/Bookings/BulkBooking";
import ResetPassword from "../pages/Users/ResetPassword";
import SharePage from "../pages/SharePage";
import MembershipsList from "../pages/Memberships/MembershipsList";
import SendNotifications from "../pages/Notifications/SendNotifications";
import StructuresPayments from "../pages/Management/StructuresPayments";
import RegisterStructure from "../pages/Structures/RegisterStructure";
import QuizList from "../pages/Quiz/QuizList";
import QuizEdit from "../pages/Quiz/QuizEdit";
import QuizDetail from "../pages/Quiz/QuizDetail";
import AssignPoints from "../pages/Points/AssignPoints";

type Props = {
  title: string;
  element: React.ReactNode;
};

function Page({ title, element }: Props) {
  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {element}
    </>
  );
}

// Path raggiungibili SENZA login (usati anche dalla guardia in AppRouter).
// Tenere questa lista allineata alle rotte pubbliche qui sotto.
export const PUBLIC_PATHS = [
  "/privacy-policy",
  "/termini-e-condizioni-d-uso",
  "/informazioni-legali",
  "/informazioni-sulla-privacy",
  "/domande-frequenti",
  "/contattaci",
  "/conferma-registrazione",
  "/reimposta-password",
];

export const APP_ROUTES: Array<RouteObject> = [
  // Pagine legali pubbliche (linkate dall'app): fuori da AppLayout, quindi
  // senza navbar/sidebar del gestionale e senza richiedere il login.
  {
    element: <PublicLayout />,
    children: [
      {
        path: "/privacy-policy",
        element: <Page title="Privacy Policy" element={<PrivacyPolicy />} />,
      },
      {
        path: "/termini-e-condizioni-d-uso",
        element: (
          <Page title="Termini e Condizioni" element={<UseConditions />} />
        ),
      },
      {
        path: "/informazioni-legali",
        element: <Page title="Informazioni Legali" element={<LegalInfo />} />,
      },
      {
        path: "/informazioni-sulla-privacy",
        element: (
          <Page title="Informazioni sulla Privacy" element={<PrivacyInfo />} />
        ),
      },
    ],
  },
  {
    path: "/",
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: (
          <Navigate to={window.__env.AFTER_LOGIN_ROUTE || "/dashboard"} />
        ),
      },
      {
        path: "/dashboard",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Dashboard" element={<Dashboard />} />,
          },
        ],
      },
      {
        path: "/admin",
        element: <AdminGuard />,
        children: [
          {
            index: true,
            element: <Navigate to="/admin/dashboard" />,
          },
          {
            path: "dashboard",
            element: <Page title="Admin Dashboard" element={<Dashboard />} />,
          },
        ],
      },
      {
        path: "/maestri",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="I tuoi Maestri" element={<CoachesList />} />,
          },
        ],
      },
      {
        path: "/dati-struttura",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="La tua Struttura" element={<StructureData />} />
            ),
          },
        ],
      },
      {
        path: "/dati-fatturazione",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page
                title="I tuoi dati di fatturazione"
                element={<BillingInfos />}
              />
            ),
          },
        ],
      },
      {
        path: "/gestione-campi",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page
                title="Gestisci i tuoi Campi"
                element={<FacilitiesList />}
              />
            ),
          },
          {
            path: "nuovo",
            element: <FacilityEdit />,
          },
          {
            path: ":id",
            element: <FacilityDetail />,
          },
          {
            path: ":id/modifica",
            element: <FacilityEdit />,
          },
        ],
      },
      {
        path: "/tornei",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page
                title="Gestisci i tuoi Tornei"
                element={<TournamentsList />}
              />
            ),
          },
          {
            path: "nuovo",
            element: <TournamentEdit />,
          },
          {
            path: ":id",
            element: <TournamentDetail />,
          },
          {
            path: ":id/modifica",
            element: <TournamentEdit />,
          },
          {
            path: ":id/generate-schedule",
            element: <GenerateSchedule />,
          },
          {
            path: ":id/schedule",
            element: <TournamentBrackets />,
          },
        ],
      },
      {
        path: "/utenti",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="I tuoi Utenti" element={<UsersList />} />,
          },
        ],
      },
      {
        path: "/prenotazioni",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Prenotazioni" element={<BookingsList />} />,
          },
          {
            path: ":facilityId/ricorrenti",
            element: <BulkBooking />,
          },
        ],
      },
      {
        path: "/abbonamenti",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="Abbonamenti" element={<MembershipsList />} />
            ),
          },
        ],
      },
      {
        path: "/registra-struttura",
        element: <AdminGuard />,
        children: [
          {
            index: true,
            element: (
              <Page
                title="Registra una Struttura"
                element={<RegisterStructure />}
              />
            ),
          },
        ],
      },
      {
        path: "/gestione-pagamenti",
        element: <AdminGuard />,
        children: [
          {
            index: true,
            element: (
              <Page
                title="Pagamenti delle Strutture"
                element={<StructuresPayments />}
              />
            ),
          },
        ],
      },
      {
        path: "/assegna-punti",
        element: <AdminGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="Assegna Get Move Points" element={<AssignPoints />} />
            ),
          },
        ],
      },
      {
        path: "/quiz",
        element: <AdminGuard />,
        children: [
          {
            index: true,
            element: <Page title="Quiz" element={<QuizList />} />,
          },
          {
            path: "nuovo",
            element: <Page title="Nuovo Quiz" element={<QuizEdit />} />,
          },
          {
            path: ":id",
            element: <Page title="Dettaglio Quiz" element={<QuizDetail />} />,
          },
          {
            path: ":id/modifica",
            element: <Page title="Modifica Quiz" element={<QuizEdit />} />,
          },
        ],
      },
      {
        path: "/invia-notifiche",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="Invia una Notifica" element={<SendNotifications />} />
            ),
          },
        ],
      },
      {
        path: "/classifica",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Classifica" element={<RankingsList />} />,
          },
        ],
      },
      {
        path: "/domande-frequenti",
        element: <Page title="Domande Frequenti" element={<FAQ />} />,
        children: [],
      },
      {
        path: "/contattaci",
        element: <Page title="Contattaci" element={<SupportForm />} />,
        children: [],
      },
      {
        path: "/conferma-registrazione",
        element: (
          <Page
            title="Conferma Registrazione"
            element={<CompleteRegistration />}
          />
        ),
        children: [],
      },
      {
        path: "/reimposta-password",
        element: (
          <Page title="Reimposta la Password" element={<ResetPassword />} />
        ),
        children: [],
      },
      {
        path: "*",
        element: <Page title="Pagina non trovata" element={<NotFound />} />,
      },
      {
        path: "/login",
        element: <Page title="Login" element={<Login />} />,
      },
      {
        path: "/Share/:id",
        element: <Page title="Scarica l'App" element={<SharePage />} />,
      },
    ],
  },
];
